import { useState, useEffect } from 'react';
import { T } from '@design/index';
import { govPageContainer } from '../ui/PatientUIGovernor';
import { useStore } from '../state/patientProductionStore';
import { useParams } from 'react-router-dom';
import type { PharmacyReceipt } from '../state/patientProductionStore';
import { patientApi } from '../api';

/* ── helpers ─────────────────────────────────────────────────── */

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function formatCurrency(n: number): string {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const PAYMENT_LABELS: Record<PharmacyReceipt['paymentMethod'], string> = {
  UPI: 'UPI',
  CARD: 'Card',
  NET_BANKING: 'Net Banking',
  COD: 'Cash on Delivery',
  WALLET: 'Wallet',
  INSURANCE: 'Insurance',
};

const PAYMENT_ICONS: Record<PharmacyReceipt['paymentMethod'], string> = {
  UPI: '📱',
  CARD: '💳',
  NET_BANKING: '🏦',
  COD: '💵',
  WALLET: '💰',
  INSURANCE: '🛡️',
};

const STATUS_STYLES: Record<PharmacyReceipt['paymentStatus'], { bg: string; color: string; label: string }> = {
  COMPLETED: { bg: `${T.success}14`, color: T.success, label: 'Paid' },
  PENDING: { bg: `${T.warning}14`, color: T.warning, label: 'Pending' },
  FAILED: { bg: `${T.danger}14`, color: T.danger, label: 'Failed' },
  REFUNDED: { bg: `${T.info}14`, color: T.info, label: 'Refunded' },
};

/* ── toast ────────────────────────────────────────────────────── */

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div style={{
      position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
      background: T.navy, color: T.white, fontFamily: T.font, fontSize: 11,
      fontWeight: 600, padding: '10px 20px', borderRadius: 10,
      boxShadow: T.shadowLg, zIndex: 9999, maxWidth: 340, textAlign: 'center',
    }}>
      {message}
      <span
        onClick={onClose}
        style={{ marginLeft: 12, cursor: 'pointer', opacity: 0.7 }}
      >
        x
      </span>
    </div>
  );
}

/* ── section helpers ──────────────────────────────────────────── */

const cardStyle: React.CSSProperties = {
  background: T.white,
  border: `1px solid ${T.border}`,
  borderRadius: 14,
  boxShadow: T.shadow,
  padding: 16,
};

const sectionTitle = (text: string): React.CSSProperties => ({
  fontSize: 10,
  fontWeight: 700,
  color: T.textSec,
  fontFamily: T.font,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.6,
  marginBottom: 10,
});

/* ── main component ───────────────────────────────────────────── */

export default function PharmacyReceiptViewer() {
  const { receiptId } = useParams<{ receiptId: string }>();
  const receipts = useStore(s => s.pharmacyReceipts);
  const [toast, setToast] = useState<string | null>(null);
  // Real patient identity from the server profile — never a placeholder that
  // could pass for real data on a printed receipt.
  const [patient, setPatient] = useState<{ name: string; nhcId: string } | null>(null);

  useEffect(() => {
    let alive = true;
    patientApi.getProfile()
      .then(p => { if (alive) setPatient({ name: p.fullName || '—', nhcId: p.nhcId || '—' }); })
      .catch(() => { if (alive) setPatient(null); });
    return () => { alive = false; };
  }, []);

  const receipt: PharmacyReceipt | undefined = receipts.find(r => r.id === receiptId);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  /* ── not found ─────────────────── */
  if (!receipt) {
    return (
      <div style={{ ...govPageContainer({ maxWidth: 440 }), padding: '40px 16px', textAlign: 'center' }}>
        <div style={{
          width: 56, height: 56, borderRadius: 16, background: `${T.danger}10`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>Receipt Not Found</div>
        <div style={{ fontSize: 11, color: T.textSec, marginBottom: 20, lineHeight: 1.5 }}>
          The receipt you are looking for does not exist or may have been removed.
        </div>
        <button
          onClick={() => { window.location.href = '/patient/pharmacy'; }}
          style={{
            background: T.navy, color: T.white, border: 'none', borderRadius: 10,
            padding: '10px 24px', fontSize: 12, fontWeight: 600, fontFamily: T.font,
            cursor: 'pointer',
          }}
        >
          Back to Orders
        </button>
      </div>
    );
  }

  const statusInfo = STATUS_STYLES[receipt.paymentStatus];

  return (
    <div style={govPageContainer({ maxWidth: 440 })}>
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      {/* ── 1. Document Header ─────────────────────────────── */}
      <div style={{
        ...cardStyle, borderRadius: 16, marginBottom: 12,
        background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyDk} 100%)`,
        border: 'none', color: T.white, position: 'relative', overflow: 'hidden',
      }}>
        {/* subtle pattern overlay */}
        <div style={{
          position: 'absolute', top: 0, right: 0, width: 120, height: 120,
          background: `radial-gradient(circle, ${T.teal}15 0%, transparent 70%)`,
          borderRadius: '0 0 0 120px',
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* logo row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, background: T.teal,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 800, color: T.white,
            }}>
              N
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3 }}>NeoHealthCard</div>
              <div style={{ fontSize: 8, opacity: 0.7, letterSpacing: 0.5 }}>DIGITAL HEALTH PLATFORM</div>
            </div>
          </div>

          {/* Not an invoice — the pharmacy issues the official GST invoice
              when it bills the order; this is the app's own acknowledgement. */}
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.3, marginBottom: 8 }}>
            Order Acknowledgement
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 8, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
              background: `${T.teal}30`, color: T.white, letterSpacing: 0.4,
              textTransform: 'uppercase',
            }}>
              Pharmacy Receipt
            </span>
            <span style={{
              fontSize: 8, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
              background: `${T.warning}30`,
              color: T.white, letterSpacing: 0.4, textTransform: 'uppercase',
            }}>
              Pending Verification
            </span>
          </div>
        </div>
      </div>

      {/* ── 2. Document Meta ──────────────────────────────── */}
      <div style={{ ...cardStyle, marginBottom: 12 }}>
        <div style={sectionTitle('Document Details')}>Document Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {([
            ['Receipt ID', receipt.id.slice(0, 12).toUpperCase()],
            ['Order ID', receipt.orderId.slice(0, 12).toUpperCase()],
            ['Date & Time', formatDateTime(receipt.createdAt)],
            ['Pharmacy', receipt.pharmacyName],
            ['Patient Name', patient?.name || '—'],
            ['NHC ID', patient?.nhcId || '—'],
          ] as [string, string][]).map(([label, value]) => (
            <div key={label}>
              <div style={{ fontSize: 8, fontWeight: 600, color: T.textTer, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>
                {label}
              </div>
              <div style={{
                fontSize: 11, fontWeight: 600, color: T.text,
                wordBreak: 'break-all',
              }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 3. Medicines Table ────────────────────────────── */}
      <div style={{ ...cardStyle, marginBottom: 12, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px 10px' }}>
          <div style={sectionTitle('Medicines')}>Medicines</div>
        </div>

        {/* table header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 36px 64px 64px',
          gap: 4, padding: '8px 16px', background: T.ice,
          borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`,
        }}>
          {['Item', 'Qty', 'Price', 'Total'].map(h => (
            <div key={h} style={{
              fontSize: 8, fontWeight: 700, color: T.textTer,
              textTransform: 'uppercase', letterSpacing: 0.5,
              textAlign: h === 'Item' ? 'left' : 'right',
            }}>
              {h}
            </div>
          ))}
        </div>

        {/* rows */}
        {receipt.medicines.map((med, i) => (
          <div
            key={i}
            style={{
              display: 'grid', gridTemplateColumns: '1fr 36px 64px 64px',
              gap: 4, padding: '10px 16px', alignItems: 'center',
              borderBottom: i < receipt.medicines.length - 1 ? `1px solid ${T.borderLight}` : 'none',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, color: T.text, lineHeight: 1.3 }}>
              {med.name}
            </div>
            <div style={{ fontSize: 11, color: T.textSec, textAlign: 'right' }}>
              {med.qty}
            </div>
            <div style={{ color: T.textSec, textAlign: 'right', fontFamily: T.mono, fontSize: 10 }}>
              {formatCurrency(med.unitPrice)}
            </div>
            <div style={{ fontWeight: 600, color: T.text, textAlign: 'right', fontFamily: T.mono, fontSize: 10 }}>
              {formatCurrency(med.subtotal)}
            </div>
          </div>
        ))}
      </div>

      {/* ── 4. Totals Section ─────────────────────────────── */}
      <div style={{ ...cardStyle, marginBottom: 12 }}>
        <div style={sectionTitle('Summary')}>Summary</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* subtotal */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: T.textSec }}>Subtotal</span>
            <span style={{ fontSize: 11, color: T.text, fontFamily: T.mono }}>{formatCurrency(receipt.subtotal)}</span>
          </div>

          {/* delivery fee — 0 means "not yet set by the pharmacy", never a
              FREE promise this app cannot make. */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: T.textSec }}>Delivery Fee</span>
            <span style={{ fontSize: 11, fontFamily: T.mono, color: T.text }}>
              {receipt.deliveryFee === 0 ? 'Confirmed by pharmacy' : formatCurrency(receipt.deliveryFee)}
            </span>
          </div>

          {/* discount */}
          {receipt.discount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: T.success }}>Discount</span>
              <span style={{ fontSize: 11, color: T.success, fontFamily: T.mono, fontWeight: 600 }}>
                -{formatCurrency(receipt.discount)}
              </span>
            </div>
          )}

          {/* tax — the pharmacy's server-computed GST rides in the order
              total; the exact split appears on the pharmacy's own invoice. */}
          {receipt.tax > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: T.textSec }}>GST (as billed by pharmacy)</span>
              <span style={{ fontSize: 11, color: T.text, fontFamily: T.mono }}>{formatCurrency(receipt.tax)}</span>
            </div>
          )}

          {/* divider */}
          <div style={{ borderTop: `1.5px dashed ${T.border}`, margin: '4px 0' }} />

          {/* total */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: `${T.navy}06`, borderRadius: 10, padding: '10px 12px', margin: '0 -4px',
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Total</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: T.navy, fontFamily: T.mono }}>
              {formatCurrency(receipt.total)}
            </span>
          </div>
        </div>
      </div>

      {/* ── 5. Payment Info ───────────────────────────────── */}
      <div style={{ ...cardStyle, marginBottom: 12 }}>
        <div style={sectionTitle('Payment')}>Payment</div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10, background: `${T.navy}08`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
            }}>
              {PAYMENT_ICONS[receipt.paymentMethod]}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                {PAYMENT_LABELS[receipt.paymentMethod]}
              </div>
              <div style={{ fontSize: 9, color: T.textTer, marginTop: 1 }}>
                {formatDate(receipt.createdAt)}
              </div>
            </div>
          </div>

          <span style={{
            fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 8,
            background: statusInfo.bg, color: statusInfo.color,
            letterSpacing: 0.3,
          }}>
            {statusInfo.label}
          </span>
        </div>
      </div>

      {/* ── 6. Document Status ────────────────────────────── */}
      {/* Store receipts are generated on this device as an order
          acknowledgement — provider verification can only come from the
          pharmacy via the backend, so this never claims "Provider Issued". */}
      <div style={{
        ...cardStyle, marginBottom: 12,
        background: `${T.warning}06`,
        border: `1px solid ${T.warning}20`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: `${T.warning}12`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.warning} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.warning }}>
              Order Acknowledgement — Pending Provider Confirmation
            </div>
            <div style={{ fontSize: 9, color: T.textSec, marginTop: 2, lineHeight: 1.4 }}>
              This receipt was generated on your device for your records. The pharmacy&apos;s verified invoice will appear once the provider issues it.
            </div>
          </div>
        </div>
      </div>

      {/* ── 7. Action Buttons ─────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* primary row */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => window.print()}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: T.navy, color: T.white, border: 'none', borderRadius: 12,
              padding: '12px 0', fontSize: 11, fontWeight: 700, fontFamily: T.font, cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download / Print
          </button>
          <button
            onClick={() => {
              const text = `Pharmacy Receipt: ${receipt.pharmacyName}\nOrder: ${receipt.orderId.slice(0, 12)}\nTotal: ${formatCurrency(receipt.total)}\nDate: ${formatDate(receipt.createdAt)}\n\nViewed on NeoHealthCard`;
              if (navigator.share) { navigator.share({ title: 'Pharmacy Receipt', text }).catch(() => {}); }
              else { navigator.clipboard.writeText(text).then(() => showToast('Receipt details copied to clipboard')).catch(() => {}); }
            }}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: T.teal, color: T.white, border: 'none', borderRadius: 12,
              padding: '12px 0', fontSize: 11, fontWeight: 700, fontFamily: T.font, cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share Receipt
          </button>
        </div>

        {/* secondary row */}
        <button
          onClick={() => { window.location.href = '/patient/pharmacy'; }}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            background: T.ice, color: T.textSec, border: `1px solid ${T.border}`,
            borderRadius: 12, padding: '11px 0', fontSize: 11, fontWeight: 600,
            fontFamily: T.font, cursor: 'pointer',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textSec} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Orders
        </button>
      </div>
    </div>
  );
}
